// Product page: variant selection, gallery, add-on fields and Add to bag / Buy now.
(() => {
  const root = document.querySelector('[data-product-purchase]');
  const dataElement = root?.querySelector('[data-product-json]');
  const gallery = document.querySelector('[data-gallery]');
  if (!root || !dataElement) return;

  const product = JSON.parse(dataElement.textContent);
  const normalise = (value) => String(value).trim().toLowerCase();

  // Gallery
  const main = gallery?.querySelector('[data-gallery-main]');
  const thumbs = gallery ? [...gallery.querySelectorAll('[data-gallery-thumb]')] : [];

  const showImage = (src, alt, activeThumb) => {
    if (!main || !src) return;
    main.src = src;
    main.alt = alt;
    thumbs.forEach((item) => item.setAttribute('aria-current', String(item === activeThumb)));
  };

  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => {
      showImage(thumb.dataset.src || '', thumb.dataset.alt || '', thumb);
      main?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  });

  const more = gallery?.querySelector('[data-gallery-more]');
  more?.addEventListener('click', () => {
    thumbs.forEach((thumb) => {
      thumb.hidden = false;
    });
    more.remove();
  });

  // Hurls photographed by grip are named grip-1 to grip-8 and lightning-grip-1 to lightning-grip-8.
  const GRIP_NUMBERS = { black: 1, white: 2, yellow: 3, 'neon yellow': 3, green: 4, red: 5, blue: 6, pink: 7, orange: 8 };
  const fileName = (src) => decodeURIComponent(src.split('?')[0].split('/').pop() || '').toLowerCase();
  const hasGripPhotos = product.images.some((image) => /^(?:lightning-)?grip-[1-8](?:[_\-.]|$)/i.test(fileName(image.src)));

  const gripImage = (variant) => {
    let colour = '';
    let gripType = '';
    product.optionNames.forEach((name, index) => {
      if (!/grip/i.test(name)) return;
      if (/colou?r/i.test(name)) colour = variant.options[index];
      else gripType = variant.options[index];
    });
    const number = GRIP_NUMBERS[normalise(colour)];
    if (!number) return null;
    const stem = `${/lightning/i.test(gripType) ? 'lightning-' : ''}grip-${number}`;
    const image = product.images.find((candidate) => {
      const name = fileName(candidate.src);
      return name === stem || name.startsWith(`${stem}_`) || name.startsWith(`${stem}.`);
    });
    if (!image) return null;
    const grip = gripType ? `${colour} ${gripType}${/grip/i.test(gripType) ? '' : ' grip'}` : `${colour} grip`;
    return { src: image.src, alt: `${product.title} with ${grip}` };
  };

  const syncGallery = (variant) => {
    const target = hasGripPhotos
      ? gripImage(variant)
      : variant.image && { src: variant.image, alt: `${product.title} – ${variant.title}` };
    if (!target) return;
    const index = product.images.findIndex((image) => fileName(image.src) === fileName(target.src));
    showImage(target.src, target.alt, index >= 1 ? thumbs[index - 1] : undefined);
  };

  // Variant selection
  const controls = [...root.querySelectorAll('[data-option-index]')];
  const livePrice = root.querySelector('[data-live-price]');

  const selectedVariant = () => {
    if (!controls.length) return product.variants[0] || null;
    const selected = [];
    controls.forEach((control) => {
      if (control.type === 'radio' && !control.checked) return;
      selected[Number(control.dataset.optionIndex)] = control.value;
    });
    return product.variants.find((variant) => variant.options.every((value, index) => value === selected[index])) || null;
  };

  const syncVariant = () => {
    const variant = selectedVariant();
    if (!variant) return;
    if (livePrice) livePrice.textContent = variant.price;
    const idInput = root.querySelector('[data-form-variant-id]');
    if (idInput) idInput.value = variant.id;
    syncGallery(variant);
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  };

  controls.forEach((control) => control.addEventListener('change', syncVariant));
  if (new URLSearchParams(window.location.search).has('variant') || hasGripPhotos) {
    const variant = selectedVariant();
    if (variant) syncGallery(variant);
  }

  // Add-on fields
  const fields = [...root.querySelectorAll('[data-option-field]')];
  const inputs = [...root.querySelectorAll('[data-option-input]')];
  const buttons = [...root.querySelectorAll('[data-purchase-action]')];
  const status = root.querySelector('[data-purchase-status]');
  const quantityInput = root.querySelector('[data-purchase-quantity]');
  let busy = false;

  root.querySelectorAll('[data-quantity-step]').forEach((button) => {
    button.addEventListener('click', () => {
      if (!quantityInput) return;
      quantityInput.value = String(Math.max(1, (Number(quantityInput.value) || 1) + Number(button.dataset.quantityStep)));
    });
  });

  // "Pairs well with" comes from Shopify's recommendations after the page has loaded.
  document.querySelectorAll('[data-complementary]').forEach(async (holder) => {
    try {
      const response = await fetch(holder.dataset.url);
      if (!response.ok) return;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = html.querySelector('[data-complementary]');
      if (fresh) holder.innerHTML = fresh.innerHTML;
    } catch {
      // The section simply stays empty.
    }
  });

  const inputsFor = (key) => inputs.filter((input) => input.dataset.optionKey === key);

  const readValue = (key) => {
    const group = inputsFor(key);
    const checked = group.find((input) => input.type === 'radio' && input.checked);
    if (checked) return checked.value.trim();
    return group.find((input) => input.type !== 'radio')?.value.trim() || '';
  };

  const conditions = (field) =>
    (field.dataset.showWhen || '')
      .split(',')
      .filter(Boolean)
      .map((condition) => condition.split('='));

  const isVisible = (field) => {
    const rules = conditions(field);
    if (!rules.length) return true;
    const matches = rules.map(([key, value]) => normalise(readValue(key)) === normalise(value));
    return field.dataset.conditionMode === 'any' ? matches.some(Boolean) : matches.every(Boolean);
  };

  const syncFields = () => {
    fields.forEach((field) => {
      if (!field.dataset.showWhen) return;
      const visible = isVisible(field);
      field.hidden = !visible;
      field.setAttribute('aria-hidden', String(!visible));
      inputsFor(field.dataset.optionField).forEach((input) => {
        input.disabled = !visible;
      });
    });
  };

  inputs.forEach((input) => input.addEventListener('change', syncFields));
  syncFields();

  const setStatus = (message, state) => {
    if (!status) return;
    status.textContent = message;
    status.dataset.state = state;
  };

  const buildItems = () => {
    const variant = selectedVariant();
    if (!variant) {
      setStatus('Please choose an available product option.', 'error');
      return null;
    }
    if (!product.available || !variant.available) {
      setStatus('That combination is currently unavailable. Please choose another option.', 'error');
      return null;
    }

    const properties = {};
    const details = [];
    const addOns = [];

    for (const field of fields) {
      const key = field.dataset.optionField;
      const label = field.dataset.optionLabel;
      if (field.dataset.optionType === 'radio') {
        const choice = inputsFor(key).find((input) => input.checked);
        if (!choice) {
          setStatus(`Please choose an option for “${label}”.`, 'error');
          return null;
        }
        properties[label] = choice.dataset.choiceLabel;
        if (choice.dataset.addonVariantId) addOns.push({ key, label, choice });
        continue;
      }

      if (!isVisible(field)) continue;
      const value = readValue(key);
      if ('required' in field.dataset && !value) {
        setStatus(`Please complete “${label}”.`, 'error');
        return null;
      }
      if (value.length > 255) {
        setStatus(`“${label}” must be 255 characters or fewer.`, 'error');
        return null;
      }
      if (value) details.push({ field, label, value });
    }

    details.forEach(({ label, value }) => {
      properties[label] = value;
    });

    const quantity = Math.max(1, Number(quantityInput?.value) || 1);
    const items = [{ id: variant.id, quantity, properties }];
    addOns.forEach(({ key, label, choice }) => {
      const addOnProperties = { 'Add-on for': product.title, 'Add-on': label };
      details
        .filter(({ field }) => conditions(field).some(([ruleKey, ruleValue]) => ruleKey === key && normalise(ruleValue) === normalise(choice.value)))
        .forEach((detail) => {
          addOnProperties[detail.label] = detail.value;
        });
      items.push({ id: Number(choice.dataset.addonVariantId), quantity, properties: addOnProperties });
    });
    return items;
  };

  const setBusy = (value) => {
    busy = value;
    buttons.forEach((button) => {
      button.disabled = value || !product.available;
      button.setAttribute('aria-busy', String(value));
    });
  };

  // The bag is handled below; the form itself is there for apps and as a no-script fallback.
  root.querySelector('[data-product-form]')?.addEventListener('submit', (event) => event.preventDefault());

  buttons.forEach((button) => {
    button.addEventListener('click', async (event) => {
      event.preventDefault();
      if (busy) return;
      const items = buildItems();
      if (!items) return;
      setBusy(true);
      setStatus('Updating your bag…', '');
      try {
        await window.MartinHurlsBag.add(items);
        if (button.dataset.purchaseAction === 'buy') {
          window.location.assign('/checkout');
          return;
        }
        window.MartinHurlsBag.open();
        setStatus('Added to your bag.', 'success');
      } catch (error) {
        setStatus(error.message || 'The bag could not be updated.', 'error');
      } finally {
        setBusy(false);
      }
    });
  });
})();
